// @flow

import * as React from 'react';
import {graphql} from 'react-apollo';
import {compose} from 'redux';
import {connect} from 'react-redux';

import seoMetaDataQuery from '../../queries/seoMetaDataQuery';
import {isSearchRoot} from '../../../server/utils/canonical';

import type {SearchQueryResults} from '../../queries/searchQuery';
import type {SearchParamsType} from '../../reducers/searchParams';
import type {State as ReduxState} from '../../reducers';
import type {
    SeoMetaData,
    SeoMetaDataResponseData
} from '../../queries/seoMetaDataQuery';

/**
 * @type{Object}
 * Is using in seoMetaDataQueryOptions function that generate params for seoMetaData query.
 */
type SeoMetaDataParams = {
    searchParams: SearchParamsType,
    results: ?SearchQueryResults,
    pathname: string
};

type StateProps = {
    gtsQuery: boolean,
    searchParams: SearchParamsType,
    pathname: string
};

type InputProps = {
    results: ?SearchQueryResults,
    resultsLoading: boolean
};

type GraphQLProps = {
    seoDataResponse: SeoMetaDataResponseData
};

type ConnectorProps = InputProps & StateProps & GraphQLProps;

/**
 * @type{Object}
 * Type to be used by components applying this HOC. Describes the injected
 * `seoData` prop.
 */
export type WithSeoMetaDataProps = {
    seoData: SeoMetaData
};

const generateSeoMetaDataParam = ({
    searchParams = {},
    results
}: SeoMetaDataParams) => ({
    filter: searchParams.filter,
    petIncluded: searchParams.petIncluded,
    currency: searchParams.currency,
    lastBreadcrumbNode:
        results && results.geography && results.geography.name
            ? results.geography.name
            : '',
    pageSize: results ? results.pageSize : '',
    page: results ? results.page : '',
    hitCount: results ? results.resultCount : '',
    adultsCount: searchParams.adultsCount || 0,
    childrenCount: searchParams.childrenCount || 0,
    maxBathrooms: searchParams.maxBathrooms,
    maxBedrooms: searchParams.maxBedrooms,
    minBathrooms: searchParams.minBathrooms,
    minBedrooms: searchParams.minBedrooms,
    maxNightlyPrice: searchParams.maxPrice,
    minNightlyPrice: searchParams.minPrice,
    lbsUuid:
        results && results.geography && results.geography.lbsId
            ? results.geography.lbsId
            : ''
});

export const seoMetaDataQueryOptions = (props: SeoMetaDataParams) => ({
    variables: {
        args: generateSeoMetaDataParam(props),
        isSearchRoot: isSearchRoot(props.pathname)
    },
    errorPolicy: 'all'
});

export const mapStateToProps = ({
    system,
    searchParams
}: ReduxState): StateProps => ({
    gtsQuery: system.gtsQuery,
    searchParams: searchParams.params,
    pathname: searchParams.pathname || ''
});

// typed for flow 0.83 via
// https://github.com/facebook/flow/blob/91e85b8457671fe6c781873e37ae317e1402c830/website/en/docs/react/hoc.md
// doesn't matter because the types don't get enforced outside :/
export default function withSeoMetaData<Props: {}>(
    WrappedComponent: React.ComponentType<Props>
): React.ComponentType<$Diff<Props, {seoData: SeoMetaData | void}>> {
    const SeoDataComponent = (props: Props & ConnectorProps) => {
        const {seoDataResponse, results, ...rest} = props;
        const {loading, error, seoMetaData} = seoDataResponse || {};
        const {
            metaKeywords,
            pageTitle,
            metaDescription,
            headlineTitle,
            searchReviewsHeadline,
            canonicalLink,
            alternativeLinks
        } = results || {};
        const seoData =
            !loading && !error && seoMetaData
                ? seoMetaData
                : {
                      metaKeywords,
                      pageTitle,
                      metaDescription,
                      headlineTitle,
                      searchReviewsHeadline,
                      canonicalLink,
                      alternativeLinks
                  };

        return <WrappedComponent {...rest} seoData={seoData} />;
    };

    return compose(
        connect(mapStateToProps),
        graphql(seoMetaDataQuery, {
            options: ({
                searchParams,
                results,
                pathname
            }: StateProps & InputProps) =>
                seoMetaDataQueryOptions({
                    searchParams,
                    results,
                    pathname
                }),
            skip: (props: StateProps & InputProps) =>
                !props.results || props.resultsLoading || !props.gtsQuery,
            name: 'seoDataResponse'
        })
    )(SeoDataComponent);
}
